import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useOutletContext, useLocation } from "react-router-dom";
import CreateCoursePopup from "../../common/createPopup";
import DeleteConfirmPopUp from "../../common/deleteConfirmPopUp";
import CourseCard from "../courseCard";
import {
  getCourses,
  addCourse,
  deleteCourse,
  updateCourse,
  getBoards,
  getClassNames,
} from "../../../helper/apicalls/course";
import "./../../../styles/admin/courses.scss";
import GlobalLoader from "../../common/GlobalLoader";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import CloseIcon from "@mui/icons-material/Close";
import Cookies from "js-cookie";

const getOptionName = (item) => {
  if (typeof item === "string") return item;
  return item?.name || item?.key || "";
};

const getCourseBoardName = (course) =>
  course?.board?.name || course?.board || "";

const getCourseClassName = (course) =>
  course?.class_name?.name ||
  course?.class_name ||
  course?.class?.name ||
  course?.class ||
  "";

const CoursesHome = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    setIsShowHeader,
    setHeaderName,
    setIsShowSearch,
    searchQuery,
    setHeaderExtra,
  } = useOutletContext() || {};
  const [openCreateCoursePopup, setCreateCoursePopup] = useState(false);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [popupHeaderTitle, setPopupHeaderTitle] = useState("Create Course");
  const [popupBtnText, setPopupBtnText] = useState("Create");
  const [popupInitialValue, setPopupInitialValue] = useState("");
  const [popupInitialRecallDays, setPopupInitialRecallDays] = useState("");
  const [popupInitialPassPercentage, setPopupInitialPassPercentage] =
    useState("");
  const [popupInitialTotalNumberQuizes, setPopupInitialTotalNumberQuizes] =
    useState("");
  const [popupInitialBoard, setPopupInitialBoard] = useState("");
  const [popupInitialClassName, setPopupInitialClassName] = useState("");
  const role = Cookies.get("role");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [boardOptions, setBoardOptions] = useState([]);
  const [classOptions, setClassOptions] = useState([]);
  const [selectedBoard, setSelectedBoard] = useState("");
  const [selectedClass, setSelectedClass] = useState("");

  const fetchCourses = async (search = "", board = "", className = "") => {
    try {
      setLoading(true);
      const data = await getCourses(search, board, className);

      if (role === "institution" || role === "teacher") {
        const normalized = Array.isArray(data)
          ? data.flatMap((item) => item?.course || [])
          : [];
        setCourses(normalized);
      } else {
        setCourses(data || []);
      }
    } catch (error) {
      console.error("Failed to fetch courses:", error);
      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  const loadFilterOptions = async () => {
    try {
      const [boardsRes, classesRes] = await Promise.all([
        getBoards(true),
        getClassNames(),
      ]);
      if (Array.isArray(boardsRes)) {
        const boardNames = boardsRes.map(getOptionName).filter(Boolean);
        setBoardOptions(boardNames);
      }
      if (Array.isArray(classesRes)) {
        const classNames = classesRes.map(getOptionName).filter(Boolean);
        setClassOptions(classNames);
      }
    } catch (err) {
      console.error("Error loading board/class filter options:", err);
    }
  };

  const handleOpenDialog = () => {
    setSelectedCourse(null);
    setPopupHeaderTitle("Create Course");
    setPopupBtnText("Create");
    setPopupInitialValue("");
    setPopupInitialRecallDays("");
    setPopupInitialPassPercentage("");
    setPopupInitialTotalNumberQuizes("");
    setPopupInitialBoard("");
    setPopupInitialClassName("");
    setCreateCoursePopup(true);
  };

  const handleCloseDialog = () => {
    setCreateCoursePopup(false);
    setSelectedCourse(null);
    setPopupHeaderTitle("Create Course");
    setPopupBtnText("Create");
    setPopupInitialValue("");
    setPopupInitialRecallDays("");
    setPopupInitialPassPercentage("");
    setPopupInitialTotalNumberQuizes("");
    setPopupInitialBoard("");
    setPopupInitialClassName("");
  };

  const handleCreateCourseSuccess = async (
    courseName,
    activeRecallIntervalDays,
    passPercentage,
    totalNumberQuizes,
    board,
    classNameVal,
  ) => {
    try {
      const newCourse = {
        name: courseName,
        active_recall_interval_days: activeRecallIntervalDays
          ? parseInt(activeRecallIntervalDays, 10)
          : null,
        pass_percentage: passPercentage ? parseInt(passPercentage, 10) : 0,
        total_number_quizes: totalNumberQuizes
          ? parseInt(totalNumberQuizes, 10)
          : 0,
        board: board || "",
        class_name: classNameVal || "",
      };

      await addCourse(newCourse);
      await fetchCourses();
      await loadFilterOptions();
      handleCloseDialog();
    } catch (error) {
      console.error("Failed to create course:", error);
    }
  };

  const handleEditCourse = (course) => {
    setSelectedCourse(course);
    setPopupHeaderTitle(`Edit ${course.name}`);
    setPopupBtnText("Update");
    setPopupInitialValue(course.name);
    setPopupInitialRecallDays(
      course.active_recall_interval_days !== undefined &&
        course.active_recall_interval_days !== null
        ? String(course.active_recall_interval_days)
        : "",
    );
    setPopupInitialPassPercentage(
      course.pass_percentage !== undefined && course.pass_percentage !== null
        ? String(course.pass_percentage)
        : "",
    );
    setPopupInitialTotalNumberQuizes(
      course.total_number_quizes !== undefined &&
        course.total_number_quizes !== null
        ? String(course.total_number_quizes)
        : "",
    );
    setPopupInitialBoard(course.board || "");
    setPopupInitialClassName(course.class_name || course.class || "");
    setCreateCoursePopup(true);
  };

  const handleUpdateCourse = async (
    updatedName,
    activeRecallIntervalDays,
    passPercentage,
    totalNumberQuizes,
    board,
    classNameVal,
  ) => {
    try {
      setLoading(true);
      const updatedCourse = {
        ...selectedCourse,
        name: updatedName,
        active_recall_interval_days:
          activeRecallIntervalDays !== undefined &&
            activeRecallIntervalDays !== null &&
            activeRecallIntervalDays !== ""
            ? parseInt(activeRecallIntervalDays, 10)
            : null,
        pass_percentage:
          passPercentage !== undefined &&
            passPercentage !== null &&
            passPercentage !== ""
            ? parseInt(passPercentage, 10)
            : 0,
        total_number_quizes:
          totalNumberQuizes !== undefined &&
            totalNumberQuizes !== null &&
            totalNumberQuizes !== ""
            ? parseInt(totalNumberQuizes, 10)
            : 0,
        board: board !== undefined ? board : selectedCourse.board || "",
        class_name:
          classNameVal !== undefined
            ? classNameVal
            : selectedCourse.class_name || selectedCourse.class || "",
      };

      await updateCourse(updatedCourse);
      await fetchCourses();
      await loadFilterOptions();
      handleCloseDialog();
    } catch (error) {
      console.error("Failed to update course:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCourse = (course) => {
    setSelectedCourse(course);
    setIsConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteCourse(selectedCourse.key);
      await fetchCourses();
      await loadFilterOptions();
      setIsConfirmDeleteOpen(false);
      setSelectedCourse(null);
    } catch (error) {
      console.error("Failed to delete course:", error);
    }
  };

  useEffect(() => {
    loadFilterOptions();
  }, []);

  const uniqueBoards = useMemo(
    () =>
      Array.from(
        new Set([
          ...boardOptions,
          ...courses.map(getCourseBoardName).filter(Boolean),
        ]),
      ).sort((a, b) => a.localeCompare(b)),
    [boardOptions, courses],
  );

  const uniqueClasses = useMemo(
    () =>
      Array.from(
        new Set([
          ...classOptions,
          ...courses.map(getCourseClassName).filter(Boolean),
        ]),
      ).sort((a, b) => a.localeCompare(b)),
    [classOptions, courses],
  );

  const [debouncedBoard, setDebouncedBoard] = useState("");
  const [debouncedClass, setDebouncedClass] = useState("");

  const filteredCourses = (courses || []).filter((course) => {
    if (selectedBoard) {
      const bName = getCourseBoardName(course).trim().toLowerCase();
      const sBoard = selectedBoard.trim().toLowerCase();
      if (bName !== sBoard) {
        return false;
      }
    }
    if (selectedClass) {
      const cName = getCourseClassName(course).trim().toLowerCase();
      const sClass = selectedClass.trim().toLowerCase();
      if (cName !== sClass) {
        return false;
      }
    }
    return true;
  });

  useEffect(() => {
    if (location.pathname === "/teacher/course-content") {
      setHeaderName("Course Content");
    } else {
      setHeaderName("Courses");
    }
  }, [setHeaderName, location.pathname, role]);

  useEffect(() => {
    setIsShowSearch(true);
  }, [location.pathname]);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchQuery || "");
      setDebouncedBoard(selectedBoard || "");
      setDebouncedClass(selectedClass || "");
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery, selectedBoard, selectedClass]);

  useEffect(() => {
    fetchCourses(debouncedSearch, debouncedBoard, debouncedClass);
  }, [debouncedSearch, debouncedBoard, debouncedClass, location.pathname]);

  useEffect(() => {
    if (!setHeaderExtra) return;
    if (uniqueBoards.length === 0 && uniqueClasses.length === 0) {
      setHeaderExtra(null);
      return;
    }
    setHeaderExtra(
      <div className="header-filters-wrapper">
        {uniqueBoards.length > 0 && (
          <div className="header-filter-group">
            <select
              id="header-board-filter"
              className={`header-filter-select board-filter ${selectedBoard ? "active" : ""}`}
              aria-label="Filter courses by board"
              value={selectedBoard}
              onChange={(e) => setSelectedBoard(e.target.value)}
            >
              <option value="">All Board</option>
              {uniqueBoards.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>
          </div>
        )}

        {uniqueClasses.length > 0 && (
          <div className="header-filter-group">
            <select
              id="header-class-filter"
              className={`header-filter-select class-filter ${selectedClass ? "active" : ""}`}
              aria-label="Filter courses by class"
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
            >
              <option value=""> All Classes</option>
              {uniqueClasses.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>,
    );

    return () => {
      setHeaderExtra(null);
    };
  }, [
    setHeaderExtra,
    selectedBoard,
    selectedClass,
    uniqueBoards,
    uniqueClasses,
  ]);

  return (
    <div className="courses-container">
      <GlobalLoader visible={loading} text="Loading courses..." />
      <div className="courses-grid">
        {role === "admin" && (
          <div
            className="course-card add-course-card"
            onClick={handleOpenDialog}
          >
            <div className="course-card-body">
              <AddCircleOutlineOutlinedIcon className="add-course-icon" />
              <h3 className="course-title">Add Course</h3>
            </div>
          </div>
        )}

        {filteredCourses?.length > 0
          ? filteredCourses?.map((course) => (
            <div key={course.id}>
              <CourseCard
                course={course}
                onEdit={handleEditCourse}
                onDelete={handleDeleteCourse}
                fetchCourses={fetchCourses}
                hideActionBtn={role !== "admin"}
                handleNavigate={() => {
                  if (role === "admin") {
                    navigate(`/admin/course/${course.key}`);
                  } else if (role === "teacher") {
                    if (location.pathname === "/teacher/course-content") {
                      navigate(`/teacher/course/${course.key}`);
                    } else {
                      navigate(
                        `/teacher/batches/info?course=${course.key}&from=courses`,
                      );
                    }
                  } else {
                    navigate(`/institution/course/${course.key}`);
                  }
                }}
              />
            </div>
          ))
          : !loading && (
            <div className="no-courses-found">
              <h3
                style={{
                  textAlign: "center",
                  width: "100%",
                  padding: "2rem",
                  color: "var(--secondary-text)",
                }}
              >
                {location.pathname === "/teacher/courses" &&
                  role === "teacher"
                  ? "No completed courses found"
                  : "No courses found"}
              </h3>
            </div>
          )}
      </div>

      {openCreateCoursePopup && (
        <CreateCoursePopup
          open={openCreateCoursePopup}
          handleClose={handleCloseDialog}
          title="Course"
          headerTitle={popupHeaderTitle}
          buttonText={popupBtnText}
          initialValue={popupInitialValue}
          initialActiveRecallIntervalDays={popupInitialRecallDays}
          initialPassPercentage={popupInitialPassPercentage}
          initialTotalNumberQuizes={popupInitialTotalNumberQuizes}
          initialBoard={popupInitialBoard}
          initialClassName={popupInitialClassName}
          onSave={
            selectedCourse ? handleUpdateCourse : handleCreateCourseSuccess
          }
        />
      )}

      {isConfirmDeleteOpen && (
        <DeleteConfirmPopUp
          isConfirmDeleteOpen={isConfirmDeleteOpen}
          setIsConfirmDeleteOpen={setIsConfirmDeleteOpen}
          handleDelete={handleConfirmDelete}
        />
      )}
    </div>
  );
};

export default CoursesHome;
